import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { ProductImage } from "../components/Bits";
import { api, errMsg, downloadFile } from "../lib/api";

const EMPTY = { code: "", name: "", category: "", unit: "Adet", price: 0, vat: 20, stock: 0, description: "", image_url: "", is_active: true };

export function ProductFormDialog({ open, onOpenChange, product, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(product ? { ...EMPTY, ...product } : EMPTY); setFile(null); }, [product, open]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, price: Number(f.price), vat: Number(f.vat), stock: Number(f.stock) };
      let { data } = product ? await api.put(`/products/${product.id}`, body) : await api.post("/products", body);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        data = (await api.post(`/products/${data.id}/image`, fd)).data;
      }
      toast.success(product ? "Ürün güncellendi" : "Ürün eklendi");
      onSaved(data);
      onOpenChange(false);
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  const fld = (k, label, type = "text", extra = {}) => (
    <div><Label className="text-xs">{label}</Label>
      <Input data-testid={`product-form-${k}-input`} type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" {...extra} /></div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{product ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          {fld("code", "Ürün Kodu *", "text", { required: true })}
          {fld("name", "Ürün Adı *", "text", { required: true })}
          {fld("category", "Kategori")}
          {fld("unit", "Birim")}
          {fld("price", "Fiyat (₺, KDV hariç)", "number", { step: "0.01", min: 0, required: true })}
          {fld("vat", "KDV %", "number", { step: "1", min: 0, max: 100 })}
          {fld("stock", "Stok", "number", { step: "1" })}
          {fld("image_url", "Fotoğraf URL (opsiyonel)")}
          <div className="col-span-2"><Label className="text-xs">Açıklama</Label>
            <Textarea data-testid="product-form-description-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1" /></div>
          <div className="col-span-2 flex items-center gap-4">
            <ProductImage product={file ? { image_url: URL.createObjectURL(file), name: "" } : f} className="h-20 w-20 rounded-lg" />
            <div className="flex-1"><Label className="text-xs">Fotoğraf Yükle</Label>
              <Input data-testid="product-form-image-input" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1" /></div>
          </div>
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Vazgeç</Button>
            <Button data-testid="product-form-save-button" disabled={busy} className="bg-slate-950 hover:bg-blue-600">{busy ? "Kaydediliyor..." : "Kaydet"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ExcelImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFile(null); setRes(null); }, [open]);
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/excel/import", fd);
      setRes(data);
      toast.success(`${data.created} ürün eklendi, ${data.updated} ürün güncellendi`);
      onDone();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="excel-import-dialog">
        <DialogHeader><DialogTitle className="font-display">Excel'den Ürün Aktar</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Sütunlar: <span className="font-mono text-xs">Ürün Kodu, Ürün Adı, Kategori, Birim, Fiyat, KDV, Stok, Açıklama, Fotoğraf URL</span>. Aynı ürün kodu varsa güncellenir.</p>
        <Button data-testid="excel-template-download-button" variant="outline" onClick={() => downloadFile("/products/excel/template", "urun_sablonu.xlsx")}>
          <Download className="mr-2 h-4 w-4" /> Şablonu İndir
        </Button>
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-8 text-sm text-slate-500 transition-colors hover:border-blue-500 hover:bg-blue-50">
          <FileSpreadsheet className="h-8 w-8 text-emerald-600" />
          {file ? <span className="font-medium text-slate-900">{file.name}</span> : "Bir .xlsx dosyası seçin"}
          <input data-testid="excel-import-file-input" type="file" accept=".xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        {res && <div data-testid="excel-import-result" className="rounded-lg bg-slate-50 p-3 text-sm">
          <b>{res.created}</b> yeni, <b>{res.updated}</b> güncellendi.{res.errors.length > 0 && <ul className="mt-1 text-rose-600">{res.errors.map((e) => <li key={e}>{e}</li>)}</ul>}
        </div>}
        <Button data-testid="excel-import-submit-button" disabled={!file || busy} onClick={upload} className="bg-slate-950 hover:bg-blue-600">
          <Upload className="mr-2 h-4 w-4" /> {busy ? "Aktarılıyor..." : "İçe Aktar"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
